// src/services/api.js (updated with bulk email method)
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 120000,
      withCredentials: true,
    });

    this.isRefreshing = false;
    this.failedQueue = [];

    this.setupInterceptors();
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("axoaccessToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        if (originalRequest.url?.includes("/auth/")) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.client(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const { axoaccessToken } = await this.refreshToken();
            localStorage.setItem("axoaccessToken", axoaccessToken);
            this.processQueue(null, axoaccessToken);
            this.isRefreshing = false;

            originalRequest.headers.Authorization = `Bearer ${axoaccessToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            this.isRefreshing = false;
            localStorage.removeItem("axoaccessToken");

            const publicPaths = [
              "/login",
              "/reset-password",
              "/forgot-password",
              "/accept-invite",
            ];
            const isPublicPath = publicPaths.some(
              (p) =>
                window.location.pathname === p ||
                window.location.pathname.startsWith(p + "?"),
            );

            if (!isPublicPath) {
              window.location.href = "/login";
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  async getSicCodes() {
    return this.client.get("/companies/sic-codes");
  }

  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  // Auth endpoints
  async signup(data) {
    const response = await this.client.post("/auth/signup", data);
    if (response.accessToken) {
      localStorage.setItem("axoaccessToken", response.accessToken);
    }
    return response;
  }

  async login(email, password) {
    const response = await this.client.post("/auth/login", { email, password });
    if (response.accessToken) {
      localStorage.setItem("axoaccessToken", response.accessToken);
    }
    return response;
  }

  async getUsers() {
    return this.client.get("/invites/users");
  }

  async logout() {
    localStorage.removeItem("axoaccessToken");
    return this.client.post("/auth/logout").catch(() => {});
  }

  async refreshToken() {
    return this.client.post("/auth/refresh");
  }

  async getCurrentUser() {
    return this.client.get("/auth/me");
  }

  // Add inside the ApiService class, near the other invite/user methods
  async adminChangeUserPassword(userId, data) {
    return this.client.patch(`/auth/${userId}/password`, data);
  }

  // Company endpoints
  // ── Live Companies House lookups (dynamic search bar; read-only, all plans)
  async searchCompaniesHouse(q, { limit = 8, signal } = {}) {
    return this.client.get("/companies/ch/search", {
      params: { q, limit },
      signal,
    });
  }

  async getCompaniesHouseCompany(companyNumber) {
    return this.client.get(
      `/companies/ch/${encodeURIComponent(companyNumber)}`,
    );
  }

  // Claim a live Companies House result as one of this customer's own
  // leads. Writes; counts against the monthly quota. All plans.
  async addCompaniesHouseCompanyToLeads(companyNumber) {
    return this.client.post(
      `/companies/ch/${encodeURIComponent(companyNumber)}/add-to-leads`,
    );
  }

  async getCompanies(params = {}) {
    return this.client.get("/companies", { params });
  }
  async getMarkets() {
    return this.client.get("/companies/markets");
  }

  async importSaCompanies(formData) {
    return this.client.post("/companies/sa/import", formData, {
      timeout: 120000,
    });
  }
  async refreshLeads() {
    return this.client.post("/subscription/refresh-leads", null, {
      timeout: 120000,
    });
  }

  async updateLeadWorkflow(companyId, data) {
    return this.client.patch(`/companies/${companyId}/workflow`, data);
  }

  async getSoleTraders(params = {}) {
    return this.client.get("/companies/sole-traders", { params });
  }

  async triggerSoleTraderDiscovery(trade, location) {
    return this.client.post(
      "/companies/sole-traders/discover",
      { trade, location },
      { timeout: 60000 },
    );
  }

  async updateEnrichmentOverride(companyId, data) {
    return this.client.post(
      `/companies/${companyId}/enrichment-override`,
      data,
    );
  }

  async exportCSV(params = {}) {
    const response = await this.client.get("/companies/export/csv", {
      params,
      responseType: "blob",
    });
    return response;
  }

  async getCompanyStats() {
    return this.client.get("/companies/stats");
  }

  async getCompanyScopes() {
    return this.client.get("/companies/scopes");
  }

  async getCompanyFacets(params = {}) {
    return this.client.get("/companies/facets", { params });
  }

  async getExcludedCompanies(params = {}) {
    return this.client.get("/companies/excluded", { params });
  }

  // Super-admin manual exclusion list (T3)
  async getCompanyExclusions(params = {}) {
    return this.client.get("/super-admin/exclusions", { params });
  }

  async addCompanyExclusion(data) {
    return this.client.post("/super-admin/exclusions", data);
  }

  async removeCompanyExclusion(id) {
    return this.client.delete(`/super-admin/exclusions/${id}`);
  }

  async getSicDescription(q) {
    return this.client.get("/companies/sic-lookup", { params: { q } });
  }

  async triggerEnrichment(companyId) {
    // Pass a config object as the third argument to override timeout
    return this.client.post(`/companies/${companyId}/enrich`, null, {
      timeout: 180000, // 3 minutes
    });
  }

  // Companies House financial filings, manual "fetch now" (bypasses
  // financialsJob's background batch/retry schedule). companyNumber is
  // the CH registration number, not the internal companyId used above.
  async refreshCompanyFinancials(companyNumber) {
    return this.client.post(`/companies/${companyNumber}/financials/refresh`);
  }

  // On-demand turnover lookup from the commercial provider (Endole) for a
  // company whose filed accounts don't report one. Grow+.
  async refreshCompanyTurnover(companyNumber) {
    return this.client.post(`/companies/${companyNumber}/turnover/refresh`);
  }

  // Re-sync runs in the background on the server (can take minutes); start it,
  // then poll until it finishes. Resolves with { data: { data } } like before.
  async resyncEnrichment(
    companyId,
    { intervalMs = 3000, maxMs = 330000 } = {},
  ) {
    await this.client.post(`/companies/${companyId}/resync`, {});
    const startedAt = Date.now();
    while (Date.now() - startedAt < maxMs) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const res = await this.client.get(
        `/companies/${companyId}/resync/status`,
      );
      const body = res; // response interceptor already unwraps .data
      if (body.status === "running") continue;
      if (body.status === "none") {
        // Job vanished (server restarted or result expired) before we saw it finish.
        throw new Error("Re-sync was interrupted, please try again");
      }
      if (body.status === "failed" || body.success === false) {
        throw new Error(body.message || "Re-sync failed");
      }
      return body;
    }
    throw new Error("Re-sync is still running, please check back shortly");
  }

  // Enrichment Override
  upsertEnrichmentOverride(companyId, data) {
    return this.client.post(
      `/companies/${companyId}/enrichment-override`,
      data,
    );
  }

  async triggerDudaBuild(companyId, { skipQA = false } = {}) {
    return this.client.post(`/companies/${companyId}/trigger-duda`, { skipQA });
  }

  async getDudaStatus(companyId) {
    return this.client.get(`/companies/${companyId}/duda-status`);
  }

  // Opportunity Scoring
  // Growth Milestones from the filed-accounts history (verified turnover
  // crossings, micro->small transitions, growth indicators).
  async getCompanyMilestones(companyId) {
    return this.client.get(`/companies/${companyId}/milestones`);
  }

  async getCompanyScore(companyId, params = {}) {
    return this.client.get(`/companies/${companyId}/score`, { params });
  }

  async batchScoreCompanies(ids, insitesScores = {}, financials = {}) {
    return this.client.post("/companies/score/batch", {
      ids,
      insites_scores: insitesScores,
      financials,
    });
  }

  // Take a fresh AXO website snapshot and re-score. Insites is retired on
  // the backend; the route keeps its historical path.
  async refreshWebsiteHealth(companyId) {
    return this.client.post(
      `/companies/${companyId}/score/insites`,
      { wait: false },
      { timeout: 60000 },
    );
  }

  // Phase 4 - QA Workflow
  async getQaQueue(qaStatus = "pending_qa", page = 1, limit = 10, search = "") {
    return this.client.get("/qa/queue", {
      params: { qaStatus, page, limit, search },
    });
  }

  async approveQa(id) {
    return this.client.post(`/qa/${id}/approve`);
  }

  async requestQaRegeneration(id, notes) {
    return this.client.post(`/qa/${id}/request-regeneration`, { notes });
  }

  // Dynamic brief-form submissions (Duda build retry, no client refill)
  async getFormSubmissions(params = {}) {
    return this.client.get("/briefs/form-submissions", { params });
  }

  async getFormSubmission(id) {
    return this.client.get(`/briefs/form-submissions/${id}`);
  }

  async retryFormSubmissionDuda(id) {
    return this.client.post(`/briefs/form-submissions/${id}/retry-duda`);
  }

  // ── Notification centre ─────────────────────────────────────────────────
  async getNotifications(params = {}) {
    return this.client.get("/notifications", { params });
  }

  async getNotificationUnreadCount() {
    return this.client.get("/notifications/unread-count");
  }

  async markNotificationRead(id) {
    return this.client.post(`/notifications/${id}/read`);
  }

  async markAllNotificationsRead() {
    return this.client.post("/notifications/read-all");
  }

  async createNotificationAnnouncement(payload) {
    return this.client.post("/notifications/announcements", payload);
  }

  // Super Admin - Customer Management
  async createCustomer(data) {
    return this.client.post("/super-admin/customers", data);
  }

  async getCustomers() {
    return this.client.get("/super-admin/customers");
  }

  async updateCustomer(customerId, data) {
    return this.client.put(`/super-admin/customers/${customerId}`, data);
  }

  async removeCustomer(customerId, confirmName) {
    return this.client.delete(`/super-admin/customers/${customerId}`, {
      data: { confirmName },
    });
  }

  async getOnboardingQueue(params = {}) {
    return this.client.get("/super-admin/onboarding", { params });
  }

  async saveOnboardingMapping(customerId, services) {
    return this.client.put(`/super-admin/onboarding/${customerId}/services`, {
      services,
    });
  }

  async completeOnboarding(customerId) {
    return this.client.post(`/super-admin/onboarding/${customerId}/complete`);
  }

  async forgotPassword(email) {
    return this.client.post("/auth/forgot-password", { email });
  }

  async resetPassword(token, password) {
    return this.client.post("/auth/reset-password", { token, password });
  }

  async getUnassignedCompanies(params = {}) {
    return this.client.get("/super-admin/companies/unassigned", { params });
  }

  async assignCompaniesToCustomer(
    customerId,
    companyIds,
    companySource = "newco",
  ) {
    return this.client.post(
      `/super-admin/customers/${customerId}/assign-companies`,
      {
        companyIds,
        companySource,
      },
    );
  }

  // Email endpoints
  async sendPreviewEmail(data) {
    return this.client.post("/email/preview", data);
  }

  async sendFollowUpEmail(data) {
    return this.client.post("/email/followup", data);
  }

  async sendIntroductionEmail(data) {
    return this.client.post("/email/intro", data);
  }
  async sendBriefEmail(data) {
    return this.client.post("/email/brief", data);
  }
  async getProfile() {
    return this.client.get("/profile");
  }
  async updateProfile(data) {
    return this.client.put("/profile", data);
  }
  async getConnections() {
    return this.client.get("/connections");
  }
  async createApiKey(data) {
    return this.client.post("/api-keys", data);
  }
  async revokeApiKey(id) {
    return this.client.delete(`/api-keys/${encodeURIComponent(id)}`);
  }
  async requestPlanUpgrade(data) {
    return this.client.post("/email/upgrade-request", data);
  }
  async cancelSubscription(data) {
    return this.client.post("/email/cancel-subscription", data);
  }

  // ── Credits (prepaid AI balance, usage, £10 top-up) ─────────────────────
  async getCredits(params = {}) {
    return this.client.get("/billing/credits", { params });
  }
  async startCreditTopUp() {
    return this.client.post("/billing/credits/top-up");
  }
  async confirmCreditTopUp(sessionId) {
    return this.client.post("/billing/credits/confirm", { sessionId });
  }

  async getSubscriptionStatus() {
    return this.client.get("/subscription/status");
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Bulk email send
  // type: 'preview' | 'followup' | 'intro' | 'brief'
  // recipients: array of objects with toEmail, firstName, companyName, customerId, companyId
  //   + previewUrl (required for preview/followup, ignored for intro)
  // templateOverride: optional { customSubject, customBody }, when provided,
  //   the backend should use this subject/body (with merge tags resolved
  //   per-recipient) instead of its standard template for this type.
  // ───────────────────────────────────────────────────────────────────────────
  async bulkSendEmails(type, recipients, templateOverride = undefined) {
    return this.client.post(
      "/email/bulk",
      {
        type,
        recipients,
        ...(templateOverride
          ? {
              customSubject: templateOverride.customSubject,
              customBody: templateOverride.customBody,
            }
          : {}),
      },
      {
        timeout: 180000,
      },
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Partner outreach (independent recruitment emails, 6ela | wizzyl)
  // recipient: { toEmail, firstName, companyName }
  // ───────────────────────────────────────────────────────────────────────────
  async sendPartnerOutreach(templateKey, recipient) {
    return this.client.post("/email/partner-outreach", {
      templateKey,
      ...recipient,
    });
  }

  async sendPartnerOutreachBulk(templateKey, recipients) {
    return this.client.post(
      "/email/partner-outreach/bulk",
      { templateKey, recipients },
      { timeout: 180000 },
    );
  }

  // Read-only render of a fixed partner template, for the bulk send
  // confirmation dialog. sample: { firstName?, companyName? }
  async getPartnerOutreachPreview(templateKey, sample = {}) {
    return this.client.get("/email/partner-outreach/preview", {
      params: { templateKey, ...sample },
    });
  }

  // Invite Management
  async inviteUser(data) {
    return this.client.post("/invites/invite", data);
  }

  async acceptInvite(token, data) {
    return this.client.post(`/invites/accept/${token}`, data);
  }

  // Payment reconciliation queue (super-admin)
  async getPaymentQueue() {
    return this.client.get("/payments/admin/queue");
  }

  async confirmPaymentManually(id, transactionId) {
    return this.client.post(`/payments/admin/${id}/confirm`, { transactionId });
  }

  async triggerReconciliationNow() {
    return this.client.post("/payments/admin/reconcile-now");
  }

  async getCustomerUsers() {
    return this.client.get("/invites/users");
  }

  async updateUserRole(userId, data) {
    return this.client.patch(`/invites/users/${userId}/role`, data);
  }

  async removeUser(userId) {
    return this.client.delete(`/invites/users/${userId}`);
  }

  async getAuditLogs(params = {}) {
    return this.client.get("/invites/audit-logs", { params });
  }

  async generateUniversalBriefLink(customerId) {
    return this.client.post("/briefs/universal-link", { customerId });
  }

  // ── Phase 5A: Revenue Intelligence ──────────────────────────────────────
  async getTodayBriefing() {
    return this.client.get("/revenue-briefing/today");
  }

  async refreshBriefing() {
    return this.client.post("/revenue-briefing/refresh");
  }

  async estimateBriefing() {
    return this.client.post("/revenue-briefing/estimate");
  }

  async rejectBriefingEstimate(logId) {
    return this.client.post("/revenue-briefing/reject-estimate", { logId });
  }

  async generateBriefing(logId) {
    return this.client.post(
      "/revenue-briefing/generate",
      logId ? { logId } : {},
      { timeout: 120000 },
    );
  }

  async getRouteSuggestions(customerCompanyIds) {
    return this.client.post("/outreach/route-suggestions", { customerCompanyIds });
  }

  async estimateOutreachDraft(customerCompanyId, draftType) {
    return this.client.post("/outreach/estimate", {
      customerCompanyId,
      draftType,
    });
  }

  async rejectOutreachEstimate(logId) {
    return this.client.post("/outreach/reject-estimate", { logId });
  }

  async prepareOutreachDraft(customerCompanyId, draftType, logId) {
    return this.client.post("/outreach/prepare", {
      customerCompanyId,
      draftType,
      ...(logId ? { logId } : {}),
    });
  }

  async getOutreachDrafts(customerCompanyId) {
    return this.client.get(`/outreach/company/${customerCompanyId}`);
  }

  async updateOutreachDraft(draftId, { subject, content }) {
    return this.client.patch(`/outreach/${draftId}`, { subject, content });
  }

  async approveAndSendOutreach(draftId) {
    return this.client.post(`/outreach/${draftId}/approve-and-send`);
  }

  async bulkApproveAndSendOutreach(draftIds) {
    return this.client.post(
      "/outreach/bulk-send",
      { draftIds },
      { timeout: 180000 },
    );
  }

  async rejectOutreach(draftId) {
    return this.client.post(`/outreach/${draftId}/reject`);
  }

  async getAiPerformanceDashboard() {
    return this.client.get("/ai-performance/dashboard");
  }

  async getAiPerformanceTimeseries(params) {
    return this.client.get("/ai-performance/timeseries", { params });
  }

  // Admin-only (internal team / superadmin).
  async getStageTimings() {
    return this.client.get("/ai-performance/stage-timings");
  }

  async getOutreachPerformance() {
    return this.client.get("/ai-performance/outreach-performance");
  }

  async getInsights() {
    return this.client.get("/ai-performance/insights");
  }

  // ── Phase 5C: Revenue Forecasting ───────────────────────────────────────
  async getForecast(customerId) {
    return this.client.get("/forecast", {
      params: customerId ? { customerId } : undefined,
    });
  }

  async recordRevenueOutcome(payload) {
    return this.client.post("/revenue-outcomes", payload);
  }

  async getPipelineBoard(customerId) {
    return this.client.get("/pipeline/board", {
      params: customerId ? { customerId } : {},
    });
  }

  async movePipelineStage(customerCompanyId, toStage, dealValueGbp) {
    return this.client.post(`/pipeline/${customerCompanyId}/stage`, {
      toStage,
      ...(dealValueGbp != null ? { dealValueGbp } : {}),
    });
  }

  async getAiGovernanceSettings() {
    return this.client.get("/ai-governance");
  }

  async seedAiGovernanceDefaults() {
    return this.client.post("/ai-governance/seed-defaults");
  }

  async updateAiGovernanceSetting(id, updates) {
    return this.client.patch(`/ai-governance/${id}`, updates);
  }

  async getBriefingSettings() {
    return this.client.get("/ai-governance/briefing-settings");
  }

  async updateBriefingSettings(payload) {
    return this.client.put("/ai-governance/briefing-settings", payload);
  }

  async getDealValues() {
    return this.client.get("/ai-governance/deal-values");
  }

  async upsertDealValue(payload) {
    return this.client.put("/ai-governance/deal-values", payload);
  }

  async deleteDealValue(id) {
    return this.client.delete(`/ai-governance/deal-values/${id}`);
  }

  // ── Phase 5, item 2/3: inbound replies ("previous conversations") ───────
  async getInboundReplies(customerCompanyId) {
    return this.client.get(`/inbound-replies/company/${customerCompanyId}`);
  }

  // ── Phase 5, item 3: company research ───────────────────────────────────
  async getCompanyDossier(customerCompanyId) {
    return this.client.get(`/company-research/${customerCompanyId}`);
  }

  async estimateCompanyResearch(customerCompanyId) {
    return this.client.post(
      `/company-research/${customerCompanyId}/summarise/estimate`,
      {},
      { timeout: 30000 },
    );
  }

  async rejectCompanyResearchEstimate(customerCompanyId, logId) {
    return this.client.post(
      `/company-research/${customerCompanyId}/summarise/reject`,
      { logId },
    );
  }

  async generateCompanyResearch(customerCompanyId, logId) {
    return this.client.post(
      `/company-research/${customerCompanyId}/summarise`,
      logId ? { logId } : {},
      { timeout: 120000 },
    );
  }

  async getLatestCompanyResearch(customerCompanyId) {
    return this.client.get(`/company-research/${customerCompanyId}/research`);
  }

  // ── Phase 5, item 5: sending beyond email ───────────────────────────────
  async getChannelActions(customerCompanyId) {
    return this.client.get(`/channel-actions/company/${customerCompanyId}`);
  }

  async recordChannelAction(payload) {
    return this.client.post("/channel-actions", payload);
  }

  async sendChannelAction(payload) {
    return this.client.post("/channel-actions/send", payload, {
      timeout: 60000,
    });
  }
}

const apiService = new ApiService();
export default apiService;
